import React, { useState } from 'react';
import { motion } from 'framer-motion';
import CourseCard from '../components/CourseCard';
import { Search, Filter, Grid, List } from 'lucide-react';

function CoursesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');

                const categories = [
                { id: 'all', name: 'All Categories' },
                { id: 'Entrepreneurship', name: 'Entrepreneurship' },
                { id: 'Business Setup', name: 'Business Setup' },
                { id: 'Global Expansion', name: 'Global Expansion' },
                { id: 'Growth & Investment', name: 'Growth & Investment' },
              ];

                const courses = [
                {
                  id: 1,
                  title: "Entrepreneurship & Business Idea Development",
                  description: "Transform your innovative ideas into profitable business ventures with proven strategies and expert guidance.",
                  category: "Entrepreneurship",
                  duration: "8 weeks",
                  students: 0,
                  rating: 4.9,
                  price: "$497",
                  level: "Beginner to Intermediate",
                  features: ["Business Model Canvas", "Market Research", "MVP Development", "Customer Validation"]
                },
                {
                  id: 2,
                  title: "Company Formation, Trade & Financial Management",
                  description: "Master the essentials of business setup, legal compliance, and financial planning for sustainable growth.",
                  category: "Business Setup",
                  duration: "6 weeks",
                  students: 0,
                  rating: 4.8,
                  price: "$397",
                  level: "Intermediate",
                  features: ["Legal Structure", "Financial Planning", "Tax Compliance", "Business Registration"]
                },
                {
                  id: 3,
                  title: "E-Export, Digital Marketing & Sales",
                  description: "Expand your business globally through digital channels and international market strategies.",
                  category: "Global Expansion",
                  duration: "10 weeks",
                  students: 0,
                  rating: 4.9,
                  price: "$597",
                  level: "Intermediate to Advanced",
                  features: ["Global Market Entry", "Digital Marketing", "E-commerce", "International Sales"]
                },
                {
                  id: 4,
                  title: "Growth, Investment & Globalization",
                  description: "Scale your business internationally and attract strategic investments for exponential growth.",
                  category: "Growth & Investment",
                  duration: "12 weeks",
                  students: 0,
                  rating: 4.9,
                  price: "$697",
                  level: "Advanced",
                  features: ["Scaling Strategies", "Investment Pitching", "Global Operations", "Strategic Partnerships"]
                }
              ];

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-6"
          >
            Explore Our Courses
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-slate-600 max-w-3xl mx-auto"
          >
            Discover our new expert-led courses designed to help you master new skills and advance your career. 
            Be among the first to explore our learning platform.
          </motion.p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search courses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-4">
              <Filter className="text-slate-400 w-5 h-5" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2 bg-slate-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors duration-200 ${
                  viewMode === 'grid' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors duration-200 ${
                  viewMode === 'list' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-8">
          <p className="text-slate-600">
            Showing {filteredCourses.length} of {courses.length} courses
          </p>
        </div>

        {/* Courses Grid */}
        <div className={`grid gap-6 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' 
            : 'grid-cols-1'
        }`}>
          {filteredCourses.map((course) => (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>

        {/* No Results */}
        {filteredCourses.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-2">No courses found</h3>
            <p className="text-slate-600 mb-6">
              Try adjusting your search terms or category filter
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('all');
              }}
              className="btn-primary"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoursesPage;
